import { 
  BatchItemInput, 
  BatchItemSimulationResult, 
  BatchSimulationSummary, 
  TaxRegimeMode 
} from './batchTaxSimulatorEngine';
import { Material, SimpleService, UserProfile } from '../types';
import { supabase } from './supabase';
import { safeStorage } from '../utils/safeStorage';

export const PENDING_SIMULATION_KEY = 'rmax_pending_batch_simulation_v1';
export const WORKSPACE_SIMULATION_PREFIX = 'rmax_batch_sim_workspace_';
export const PENDING_CHECKOUT_PLAN_KEY = 'rmax_pending_checkout_plan';
export const EXIT_INTENT_SHOWN_KEY = 'rmax_batch_sim_exit_intent_shown';

export interface SimulationConfigState {
  taxRegime: TaxRegimeMode;
  targetMarginPct: number;
  taxBenchmark: 'tcu_2026' | 'fazenda_preliminar_8_8';
  fileName: string;
  updatedAt: string;
}

export type CostCenterExportMode = 'original' | 'simulated';

export interface SavedBatchSimulationBundle {
  version?: 1;
  id?: string;
  userId?: string;
  savedAt?: string;
  originalItems: BatchItemInput[];
  simulatedItems?: BatchItemInput[] | BatchItemSimulationResult[];
  results?: BatchItemSimulationResult[];
  summary?: BatchSimulationSummary | null;
  taxRegime?: TaxRegimeMode;
  targetMarginPct?: number;
  taxBenchmark?: 'tcu_2026' | 'fazenda_preliminar_8_8';
  fileName?: string;
  config?: SimulationConfigState;
}

export const storePendingSimulation = (bundle: SavedBatchSimulationBundle) => saveSimulationBundle(bundle);
export const setPendingCheckoutPlan = (planKey: string) => stagePendingCheckoutPlan(planKey);

/**
 * Salva o pacote de simulação (Original vs Simulado) no storage adequado.
 */
export function saveSimulationBundle(
  bundle: Omit<SavedBatchSimulationBundle, 'version'>,
  userId?: string
): void {
  const fullBundle: SavedBatchSimulationBundle = {
    ...bundle,
    version: 1,
    userId: userId || undefined,
  };

  const serialized = JSON.stringify(fullBundle);
  try {
    if (userId && userId !== 'guest') {
      safeStorage.setItem(`${WORKSPACE_SIMULATION_PREFIX}${userId}`, serialized);
    } else {
      safeStorage.setItem(PENDING_SIMULATION_KEY, serialized);
    }
  } catch (err) {
    console.warn('[batchSimulatorStorageBridge] Falha ao salvar bundle:', err);
  }
}

/**
 * Carrega a simulação salva. Se houver userId, busca primeiro no workspace do usuário.
 */
export function loadSimulationBundle(userId?: string): SavedBatchSimulationBundle | null {
  try {
    if (userId && userId !== 'guest') {
      const userRaw = safeStorage.getItem(`${WORKSPACE_SIMULATION_PREFIX}${userId}`);
      if (userRaw) {
        return JSON.parse(userRaw) as SavedBatchSimulationBundle;
      }
    }

    const pendingRaw = safeStorage.getItem(PENDING_SIMULATION_KEY);
    if (pendingRaw) {
      return JSON.parse(pendingRaw) as SavedBatchSimulationBundle;
    }
  } catch (err) {
    console.warn('[batchSimulatorStorageBridge] Falha ao carregar bundle:', err);
  }
  return null;
}

/**
 * Limpa a simulação armazenada.
 */
export function clearSimulationBundle(userId?: string): void {
  try {
    if (userId && userId !== 'guest') {
      safeStorage.removeItem(`${WORKSPACE_SIMULATION_PREFIX}${userId}`);
    }
    safeStorage.removeItem(PENDING_SIMULATION_KEY);
  } catch (err) {
    console.warn('[batchSimulatorStorageBridge] Falha ao limpar bundle:', err);
  }
}

/**
 * Verifica se há rascunho de visitante pendente.
 */
export function hasPendingVisitorSimulation(): boolean {
  try {
    const raw = safeStorage.getItem(PENDING_SIMULATION_KEY);
    return Boolean(raw && raw.length > 20);
  } catch {
    return false;
  }
}

/**
 * Reivindica o rascunho de visitante para o usuário logado (evita perda de dados).
 */
export function claimPendingSimulationForUser(userId: string): SavedBatchSimulationBundle | null {
  if (!userId || userId === 'guest') return null;
  try {
    const pendingRaw = safeStorage.getItem(PENDING_SIMULATION_KEY);
    if (!pendingRaw) return null;

    const bundle = JSON.parse(pendingRaw) as SavedBatchSimulationBundle;
    bundle.userId = userId;

    safeStorage.setItem(`${WORKSPACE_SIMULATION_PREFIX}${userId}`, JSON.stringify(bundle));
    safeStorage.removeItem(PENDING_SIMULATION_KEY);
    return bundle;
  } catch (err) {
    console.warn('[batchSimulatorStorageBridge] Falha ao reivindicar rascunho de visitante:', err);
    return null;
  }
}

/**
 * Guarda plano de checkout selecionado para prosseguir pós-autenticação.
 */
export function stagePendingCheckoutPlan(planKey: string): void {
  try {
    safeStorage.setItem(PENDING_CHECKOUT_PLAN_KEY, planKey.trim());
  } catch {}
}

/**
 * Consome e remove o plano de checkout pendente.
 */
export function consumePendingCheckoutPlan(): string | null {
  try {
    const p = safeStorage.getItem(PENDING_CHECKOUT_PLAN_KEY);
    if (p) {
      safeStorage.removeItem(PENDING_CHECKOUT_PLAN_KEY);
      return p;
    }
  } catch {}
  return null;
}

/**
 * Verifica se o usuário é cliente R-MAX ativo ou isento de modal de saída.
 */
export function isExemptOrActiveRmaxCustomer(user?: UserProfile | null, sessionUser?: any): boolean {
  if (!user && !sessionUser) return false;
  if (user?.isMasterUser) return true;
  if (user?.isTrialActive) return true;
  if (user?.subscriptionStatus === 'active') return true;

  const plan = (user?.plan || '').toLowerCase();
  if (plan === 'pro' || plan === 'especialista' || plan === 'empresa' || plan === 'enterprise') {
    return true;
  }
  return false;
}

/**
 * Converte materiais e serviços já existentes no banco R-MAX para itens do simulador.
 */
export function loadCatalogItemsIntoSimulator(
  materials: Material[],
  services: SimpleService[]
): BatchItemInput[] {
  const items: BatchItemInput[] = [];

  // 1. Produtos / Materiais de Revenda ou Insumos
  materials.forEach((m, idx) => {
    const isService = m.category === 'fixo' && m.yieldAmount === 0;
    const isProduct = !isService;

    items.push({
      id: m.id || `mat_${idx}`,
      sku: m.sku || m.barcode || `SKU-M${idx + 1}`,
      name: m.name,
      itemType: isProduct ? 'product' : 'service',
      fiscalCode: m.ncm || (isProduct ? '0000.00.00' : '1.0101.10.00'),
      ncm: m.ncm || (isProduct ? '0000.00.00' : undefined),
      costPrice: Number(m.pricePaid || 0),
      currentSalePrice: Number(m.salePrice || (m.pricePaid ? m.pricePaid * 1.4 : 0)),
      supplierTaxRegime: 'regime_geral',
      taxReduction: m.isCbna ? 'zero' : undefined,
    });
  });

  // 2. Serviços Simples cadastrados
  services.forEach((s, idx) => {
    items.push({
      id: s.id || `srv_${idx}`,
      sku: `SRV-${idx + 1}`,
      name: s.name,
      itemType: 'service',
      fiscalCode: '1.2501.10.00',
      nbs: '1.2501.10.00',
      costPrice: Number(s.value ? s.value * 0.35 : 0),
      currentSalePrice: Number(s.value || 0),
      supplierTaxRegime: 'regime_geral',
      taxReduction: 'padrao',
    });
  });

  return items;
}

/**
 * Consulta itens modelo públicos (niche_templates e niche_service_templates)
 * que já existem no banco de dados para demonstrar o catálogo R-MAX com dados reais.
 */
export async function fetchDatabaseTemplatesForSimulation(niche?: string): Promise<BatchItemInput[]> {
  const items: BatchItemInput[] = [];

  try {
    let matQuery = supabase.from('niche_templates').select('*').limit(25);
    let srvQuery = supabase.from('niche_service_templates').select('*').limit(15);

    if (niche && niche.trim().length > 0 && niche !== 'Geral') {
      matQuery = matQuery.ilike('nicho', `%${niche.trim()}%`);
      srvQuery = srvQuery.ilike('nicho', `%${niche.trim()}%`);
    }

    const [matRes, srvRes] = await Promise.all([matQuery, srvQuery]);

    if (matRes.data && Array.isArray(matRes.data)) {
      matRes.data.forEach((row: any, idx: number) => {
        const estCost = row.categoria === 'Equipamento' ? 120 : 35;
        items.push({
          id: `tpl_m_${row.id || idx}`,
          sku: `TPL-MAT-${idx + 1}`,
          name: row.nome_item || `Material ${idx + 1}`,
          itemType: 'product',
          fiscalCode: '0000.00.00',
          costPrice: estCost,
          currentSalePrice: Number((estCost * 1.5).toFixed(2)),
          supplierTaxRegime: 'regime_geral',
          taxReduction: 'padrao',
        });
      });
    }

    if (srvRes.data && Array.isArray(srvRes.data)) {
      srvRes.data.forEach((row: any, idx: number) => {
        items.push({
          id: `tpl_s_${row.id || idx}`,
          sku: `TPL-SRV-${idx + 1}`,
          name: row.nome_servico || `Serviço ${idx + 1}`,
          itemType: 'service',
          fiscalCode: '1.2501.10.00',
          nbs: '1.2501.10.00',
          costPrice: 40.0,
          currentSalePrice: 120.0,
          supplierTaxRegime: 'regime_geral',
          taxReduction: 'padrao',
        });
      });
    }
  } catch (err) {
    console.warn('[batchSimulatorStorageBridge] Falha ao carregar templates do banco:', err);
  }

  return items;
}

export interface ExportToCostCenterOptions {
  items?: BatchItemSimulationResult[];
  results?: BatchItemSimulationResult[];
  originalItems?: BatchItemInput[];
  mode: CostCenterExportMode;
  existingMaterials?: Material[];
  existingServices?: SimpleService[];
  onSaveMaterials?: (m: Material[]) => Promise<void>;
  onSaveService?: (s: SimpleService) => Promise<any>;
  userId?: string;
}

export interface ExportBatchResult {
  productsCount: number;
  servicesCount: number;
  materials: Material[];
  services: SimpleService[];
}

/**
 * Exporta os itens para o Centro de Custos do R-MAX.
 * 
 * Separação Estrita de Segurança:
 * - Modo 'original' (Padrão Seguro): Salva custo original e preço original,
 *   preservando os preços atuais da empresa e guardando o estudo fiscal apenas
 *   nos metadados (nicheMetadata) para histórico e auditoria.
 * - Modo 'simulated' (Opcional): Aplica os novos preços simulados e créditos apurados.
 */
export async function exportBatchToCostCenter(
  itemsOrOptions: BatchItemSimulationResult[] | ExportToCostCenterOptions,
  originalItemsOrMode?: BatchItemInput[] | CostCenterExportMode,
  modeParam?: CostCenterExportMode
): Promise<ExportBatchResult> {
  let items: BatchItemSimulationResult[];
  let mode: CostCenterExportMode;
  let existingMaterials: Material[] = [];
  let existingServices: SimpleService[] = [];
  let onSaveMaterials: ((m: Material[]) => Promise<void>) | undefined;
  let onSaveService: ((s: SimpleService) => Promise<any>) | undefined;
  let userId: string = 'local';

  if (Array.isArray(itemsOrOptions)) {
    items = itemsOrOptions;
    mode = (typeof originalItemsOrMode === 'string' ? originalItemsOrMode : modeParam) || 'original';
  } else {
    items = itemsOrOptions.items || itemsOrOptions.results || [];
    mode = itemsOrOptions.mode;
    existingMaterials = itemsOrOptions.existingMaterials || [];
    existingServices = itemsOrOptions.existingServices || [];
    onSaveMaterials = itemsOrOptions.onSaveMaterials;
    onSaveService = itemsOrOptions.onSaveService;
    userId = itemsOrOptions.userId || 'local';
  }

  let productsCount = 0;
  let servicesCount = 0;

  const updatedMaterials = [...existingMaterials];
  const updatedServices = [...existingServices];

  for (const item of items) {
    const isService = item.fiscalType === 'NBS';

    if (isService) {
      // Exportação / Atualização de Serviço
      const existingIdx = updatedServices.findIndex(
        s => s.name.trim().toLowerCase() === item.name.trim().toLowerCase()
      );

      const targetValue = mode === 'simulated' ? item.suggestedSalePrice : item.currentSalePrice;

      if (existingIdx >= 0) {
        const existing = updatedServices[existingIdx];
        const updated = {
          ...existing,
          value: targetValue,
          description: `${existing.description || ''}\n[Reforma Tributária]: Alíquota Saída ${item.totalTaxRatePct.toFixed(1)}% (${item.reductionLabel})`.trim(),
        };
        updatedServices[existingIdx] = updated;
        if (onSaveService) {
          await onSaveService(updated);
        }
      } else {
        const newService: SimpleService = {
          id: crypto.randomUUID(),
          userId,
          name: item.name,
          value: targetValue,
          type: 'fixed',
          description: `Item importado do Simulador da Reforma Tributária. Alíquota Saída: ${item.totalTaxRatePct.toFixed(1)}% (${item.reductionLabel})`,
          createdAt: new Date().toISOString(),
        };
        updatedServices.push(newService);
        if (onSaveService) {
          await onSaveService(newService);
        }
      }
      servicesCount++;
    } else {
      // Exportação / Atualização de Produto / Material
      const existingIdx = updatedMaterials.findIndex(
        m => m.name.trim().toLowerCase() === item.name.trim().toLowerCase() ||
             (m.sku && item.sku && m.sku.trim().toUpperCase() === item.sku.trim().toUpperCase())
      );

      const resolvedCost = mode === 'simulated' ? (item.netStockCost || item.originalCost) : item.originalCost;
      const resolvedSalePrice = mode === 'simulated' ? item.suggestedSalePrice : item.currentSalePrice;

      const taxScenarioMeta = {
        taxReformScenario: {
          originalCost: item.originalCost,
          originalPrice: item.currentSalePrice,
          suggestedPrice: item.suggestedSalePrice,
          simulatedPrice: item.simulatedSalePrice,
          fiscalCode: item.fiscalCode,
          fiscalType: item.fiscalType,
          inputCreditTotal: item.inputCreditVal,
          cbsCredit: item.inputCreditCbsVal,
          ibsCredit: item.inputCreditIbsVal,
          taxOnSaleTotal: item.taxOnSaleVal,
          netTaxToPay: item.netTaxPayableVal,
          reductionLabel: item.reductionLabel,
          appliedMode: mode,
          savedAt: new Date().toISOString(),
        }
      };

      if (existingIdx >= 0) {
        const existing = updatedMaterials[existingIdx];
        updatedMaterials[existingIdx] = {
          ...existing,
          pricePaid: resolvedCost > 0 ? resolvedCost : existing.pricePaid,
          salePrice: resolvedSalePrice,
          ncm: item.fiscalCode || existing.ncm,
          sku: item.sku || existing.sku,
          cbsCredit: mode === 'simulated' ? item.inputCreditCbsVal : existing.cbsCredit,
          ibsCredit: mode === 'simulated' ? item.inputCreditIbsVal : existing.ibsCredit,
          netCost: mode === 'simulated' ? item.netStockCost : existing.netCost,
          nicheMetadata: {
            ...(existing.nicheMetadata || {}),
            ...taxScenarioMeta,
          },
        };
      } else {
        updatedMaterials.push({
          id: crypto.randomUUID(),
          userId,
          name: item.name,
          category: 'variável',
          itemType: 'produto_venda',
          unit: 'un',
          yieldAmount: 1,
          pricePaid: resolvedCost,
          salePrice: resolvedSalePrice,
          stockQuantity: 1,
          ncm: item.fiscalCode || undefined,
          sku: item.sku || undefined,
          cbsCredit: mode === 'simulated' ? item.inputCreditCbsVal : undefined,
          ibsCredit: mode === 'simulated' ? item.inputCreditIbsVal : undefined,
          netCost: mode === 'simulated' ? item.netStockCost : undefined,
          nicheMetadata: taxScenarioMeta,
        });
      }
      productsCount++;
    }
  }

  if (productsCount > 0 && onSaveMaterials) {
    await onSaveMaterials(updatedMaterials);
  }

  return { productsCount, servicesCount, materials: updatedMaterials, services: updatedServices };
}
